import { FormEvent, useState } from 'react';
import type { FieldValue, Validator } from '../utils/validation';

export type SubmissionStatus = 'idle' | 'invalid' | 'not-connected';

export function useForm<T extends Record<string, FieldValue>>(initial: T, validators: Partial<Record<keyof T, Validator>>) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [status, setStatus] = useState<SubmissionStatus>('idle');

  const setField = <K extends keyof T>(key: K, value: T[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    if (status === 'not-connected') setStatus('idle');
  };

  const setFieldError = (key: keyof T, message: string | undefined) => setErrors((prev) => ({ ...prev, [key]: message }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next: Partial<Record<keyof T, string>> = {};
    (Object.keys(validators) as (keyof T)[]).forEach((key) => {
      const message = validators[key]?.(values[key]);
      if (message) next[key] = message;
    });
    const merged = { ...next };
    (Object.keys(errors) as (keyof T)[]).forEach((key) => {
      if (errors[key] && !merged[key]) merged[key] = errors[key];
    });
    setErrors(merged);
    const firstInvalid = (Object.keys(merged) as (keyof T)[]).find((k) => merged[k]);
    if (firstInvalid) {
      setStatus('invalid');
      event.currentTarget.querySelector<HTMLElement>(`[name="${String(firstInvalid)}"]`)?.focus();
      return;
    }
    // No submission backend is configured, so nothing is transmitted.
    setStatus('not-connected');
  };

  const reset = () => {
    setValues(initial);
    setErrors({});
    setStatus('idle');
  };

  return { values, errors, status, setField, setFieldError, handleSubmit, reset };
}
